<template>
  <div class="password-gate-shell">
    <div class="password-gate-glow glow-one"></div>
    <div class="password-gate-glow glow-two"></div>

    <div class="password-gate-card">
      <div class="gate-badge">
        <i class="fas fa-shield-halved"></i>
        访问受限
      </div>

      <h1>请输入访问密码</h1>
      <p class="gate-desc">
        这个站点已启用密码保护，输入正确密码后才能继续访问。
      </p>

      <form class="gate-form" @submit.prevent="handleSubmit">
        <label class="gate-label" for="site-password">密码</label>
        <div class="password-field">
          <input
            ref="passwordInput"
            id="site-password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            autocomplete="current-password"
            placeholder="请输入访问密码"
            @input="errorMessage = ''"
          />
          <button
            type="button"
            class="toggle-password-btn"
            @click="showPassword = !showPassword"
          >
            <i class="fas" :class="showPassword ? 'fa-eye-slash' : 'fa-eye'"></i>
          </button>
        </div>

        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

        <button class="unlock-btn" type="submit" :disabled="isSubmitting">
          <i class="fas fa-unlock"></i>
          {{ isSubmitting ? '验证中...' : '进入网站' }}
        </button>
      </form>
        <p class="gate-footnote">
          访问状态会保存在本机浏览器中，并在默认 30 天后自动过期。
        </p>
    </div>
  </div>
</template>

<script>
import { unlockSite } from '@/utils/siteAuth'

export default {
  name: 'SitePasswordGate',
  emits: ['unlocked'],
  data() {
    return {
      password: '',
      showPassword: false,
      isSubmitting: false,
      errorMessage: '',
    }
  },
  mounted() {
    this.focusPasswordInput()
  },
  methods: {
    focusPasswordInput() {
      this.$nextTick(() => {
        if (this.$refs.passwordInput) {
          this.$refs.passwordInput.focus()
        }
      })
    },
    handleSubmit() {
      this.isSubmitting = true
      this.errorMessage = ''

      try {
        const result = unlockSite(this.password)
        if (!result.success) {
          this.errorMessage = result.message
          return
        }

        this.$emit('unlocked')
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style scoped>
.password-gate-shell {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.25), transparent 32%),
    radial-gradient(circle at bottom right, rgba(16, 185, 129, 0.18), transparent 36%),
    linear-gradient(160deg, #07111f 0%, #0f172a 48%, #111827 100%);
}

.password-gate-glow {
  position: absolute;
  border-radius: 999px;
  filter: blur(24px);
  opacity: 0.7;
  pointer-events: none;
}

.glow-one {
  width: 22rem;
  height: 22rem;
  background: rgba(59, 130, 246, 0.22);
  top: -6rem;
  left: -5rem;
}

.glow-two {
  width: 18rem;
  height: 18rem;
  background: rgba(14, 165, 233, 0.18);
  right: -4rem;
  bottom: -5rem;
}

.password-gate-card {
  position: relative;
  z-index: 1;
  width: min(92vw, 460px);
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  background: rgba(8, 15, 28, 0.78);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.42);
  backdrop-filter: blur(18px);
  color: #e5eefc;
}

.gate-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.18);
  color: #93c5fd;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.password-gate-card h1 {
  margin: 1rem 0 0.65rem;
  font-size: clamp(1.8rem, 4vw, 2.35rem);
  line-height: 1.1;
}

.gate-desc {
  margin: 0 0 1.5rem;
  color: rgba(229, 238, 252, 0.78);
  line-height: 1.7;
}

.gate-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.gate-label {
  font-size: 0.9rem;
  color: rgba(229, 238, 252, 0.88);
}

.password-field {
  position: relative;
  display: flex;
  align-items: center;
}

.password-field input {
  width: 100%;
  padding: 0.95rem 3rem 0.95rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.9);
  color: #f8fafc;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}

.password-field input::placeholder {
  color: rgba(148, 163, 184, 0.85);
}

.password-field input:focus {
  border-color: rgba(96, 165, 250, 0.9);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
}

.toggle-password-btn {
  position: absolute;
  right: 0.75rem;
  border: none;
  background: transparent;
  color: rgba(226, 232, 240, 0.72);
  cursor: pointer;
  padding: 0.25rem;
}

.toggle-password-btn:hover {
  color: #fff;
}

.error-message {
  margin: -0.15rem 0 0;
  color: #fca5a5;
  font-size: 0.9rem;
}

.unlock-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  margin-top: 0.25rem;
  padding: 0.95rem 1.1rem;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #38bdf8 0%, #2563eb 100%);
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
}

.unlock-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 18px 30px rgba(37, 99, 235, 0.3);
}

.unlock-btn:disabled {
  opacity: 0.75;
  cursor: wait;
  transform: none;
  box-shadow: none;
}

.gate-footnote {
  margin: 1rem 0 0;
  color: rgba(148, 163, 184, 0.92);
  font-size: 0.82rem;
  line-height: 1.6;
}

.gate-footnote span {
  color: #dbeafe;
  font-weight: 600;
}

@media (max-width: 640px) {
  .password-gate-card {
    padding: 1.5rem;
    border-radius: 20px;
  }
}
</style>
